/**
 * Level-Up Wizard
 *
 * Multi-step modal wizard for D&D 5e character leveling.
 * Steps: Class → HP → ASI/Feat → Subclass → Features → Spells → Review
 */

import { useState } from 'react';
import { Icon } from './Icon';
import { useCharacterStore } from '../stores/characterStore';
import {
  getLevelUpPreview,
  canLevelUp,
  type LevelUpChoices,
  type LevelUpPreview,
  type AsiChoice,
  type FeatChoice,
} from '@machina/core';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

const ABILITIES = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const;
const ABILITY_SHORT: Record<string, string> = {
  strength: 'STR', dexterity: 'DEX', constitution: 'CON',
  intelligence: 'INT', wisdom: 'WIS', charisma: 'CHA',
};

interface LevelUpWizardProps {
  characterId: string;
  onClose: () => void;
  onComplete?: () => void;
}

export function LevelUpWizard({ characterId, onClose, onComplete }: LevelUpWizardProps) {
  const character = useCharacterStore((s) => s.characters.get(characterId));
  const levelUp = useCharacterStore((s) => s.levelUp);

  // Step state
  const [step, setStep] = useState(0);
  const [classIndex, setClassIndex] = useState(0); // Which class to level
  const [preview, setPreview] = useState<LevelUpPreview | null>(null);

  // Choices
  const [hpMethod, setHpMethod] = useState<'average' | 'roll'>('average');
  const [hpRoll, setHpRoll] = useState<number | null>(null);
  const [asiMode, setAsiMode] = useState<'asi' | 'feat'>('asi');
  const [asi1, setAsi1] = useState<string>('strength');
  const [asi2, setAsi2] = useState<string>('strength');
  const [featName, setFeatName] = useState('');
  const [subclass, setSubclass] = useState('');
  const [newSpells, setNewSpells] = useState<string[]>([]);
  const [newSpellInput, setNewSpellInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!character || !canLevelUp(character)) {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {!character ? 'Character not found.' : 'Not enough XP to level up.'}
          </p>
          <button onClick={onClose} style={buttonStyle}>Close</button>
        </div>
      </div>
    );
  }

  // Initialize preview on first render
  if (!preview) {
    const p = getLevelUpPreview(character, classIndex);
    setPreview(p);
  }

  // Build steps list (some are conditional)
  const steps: string[] = ['Class', 'HP'];
  if (preview?.needsAsi) steps.push('ASI / Feat');
  if (preview?.needsSubclass) steps.push('Subclass');
  steps.push('Features');
  if (preview?.needsSpellSelection) steps.push('Spells');
  steps.push('Review');

  const handleClassSelect = (idx: number) => {
    setClassIndex(idx);
    setPreview(getLevelUpPreview(character, idx));
  };

  const handleRollHp = () => {
    if (!preview) return;
    const roll = Math.floor(Math.random() * preview.hitDie) + 1;
    setHpRoll(roll);
    setHpMethod('roll');
  };

  const handleConfirm = () => {
    if (!preview || isSubmitting) return;
    setIsSubmitting(true);

    // Build ASI choice
    let finalAsi: AsiChoice | FeatChoice | undefined;
    if (preview.needsAsi) {
      if (asiMode === 'asi') {
        finalAsi = { type: 'asi', ability1: asi1, ability2: asi2 };
      } else if (featName.trim()) {
        finalAsi = { type: 'feat', featName: featName.trim() };
      }
    }

    const choices: LevelUpChoices = {
      classIndex,
      hpMethod,
      hpRollResult: hpMethod === 'roll' ? (hpRoll ?? undefined) : undefined,
      asi: finalAsi,
      subclass: preview.needsSubclass ? subclass.trim() || undefined : undefined,
      newSpells: newSpells.length > 0 ? newSpells : undefined,
    };

    levelUp(characterId, choices);
    onComplete?.();
    onClose();
  };

  const nextStep = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const hpGain = hpMethod === 'roll' && hpRoll != null
    ? Math.max(1, hpRoll + (preview?.conMod ?? 0))
    : Math.max(1, (preview?.hpAverage ?? 5) + (preview?.conMod ?? 0));

  // =========================================================================
  // RENDER
  // =========================================================================

  const renderStep = () => {
    const stepName = steps[step];

    switch (stepName) {
      case 'Class':
        return (
          <div>
            <h3 style={sectionTitle}>Choose Class to Level Up</h3>
            {character.classes.map((cls, i) => (
              <button
                key={i}
                onClick={() => handleClassSelect(i)}
                style={{
                  ...classCardStyle,
                  borderColor: classIndex === i ? GOLD : 'var(--color-border)',
                  backgroundColor: classIndex === i ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                }}
              >
                <span style={{ fontWeight: 600, color: classIndex === i ? GOLD : 'var(--color-text)' }}>
                  {cls.name} {cls.level}
                </span>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                  → Level {cls.level + 1}
                </span>
              </button>
            ))}
            {preview && preview.canMulticlass.length > 0 && (
              <>
                <div style={{ ...sectionTitle, marginTop: 20, fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Or multiclass into...
                </div>
                {preview.canMulticlass.map(({ className, meetsPrereqs }) => (
                  <button
                    key={className}
                    disabled={!meetsPrereqs}
                    onClick={() => {
                      // Add new class temporarily for preview
                      const newIdx = character.classes.length;
                      setClassIndex(newIdx);
                      // Can't preview multiclass without the class existing; show basic info
                      setPreview({
                        ...preview,
                        classBeingLeveled: className,
                        newClassLevel: 1,
                        needsSubclass: false,
                        needsAsi: false,
                        newFeatures: [],
                      });
                    }}
                    style={{
                      ...classCardStyle,
                      opacity: meetsPrereqs ? 1 : 0.4,
                      cursor: meetsPrereqs ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <span>{className}</span>
                    {!meetsPrereqs && (
                      <span style={{ color: '#dc143c', fontSize: 11 }}>Prerequisites not met</span>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        );

      case 'HP':
        return (
          <div>
            <h3 style={sectionTitle}>Hit Point Gain</h3>
            <p style={descStyle}>
              {preview?.classBeingLeveled} uses a d{preview?.hitDie}. CON modifier: {(preview?.conMod ?? 0) >= 0 ? '+' : ''}{preview?.conMod ?? 0}
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button
                onClick={() => { setHpMethod('average'); setHpRoll(null); }}
                style={{
                  ...optionButtonStyle,
                  borderColor: hpMethod === 'average' ? GOLD : 'var(--color-border)',
                  backgroundColor: hpMethod === 'average' ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                }}
              >
                <div style={{ fontWeight: 600, color: hpMethod === 'average' ? GOLD : 'var(--color-text)' }}>
                  Take Average
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: GOLD, marginTop: 4 }}>
                  +{Math.max(1, (preview?.hpAverage ?? 5) + (preview?.conMod ?? 0))} HP
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {preview?.hpAverage} + {(preview?.conMod ?? 0) >= 0 ? '+' : ''}{preview?.conMod ?? 0} CON
                </div>
              </button>
              <button
                onClick={handleRollHp}
                style={{
                  ...optionButtonStyle,
                  borderColor: hpMethod === 'roll' ? GOLD : 'var(--color-border)',
                  backgroundColor: hpMethod === 'roll' ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                }}
              >
                <div style={{ fontWeight: 600, color: hpMethod === 'roll' ? GOLD : 'var(--color-text)' }}>
                  Roll d{preview?.hitDie}
                </div>
                {hpRoll != null ? (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700, color: GOLD, marginTop: 4 }}>
                      +{Math.max(1, hpRoll + (preview?.conMod ?? 0))} HP
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                      Rolled {hpRoll} + {(preview?.conMod ?? 0) >= 0 ? '+' : ''}{preview?.conMod ?? 0} CON
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: 'var(--color-text-muted)', marginTop: 8 }}>
                    Click to roll
                  </div>
                )}
              </button>
            </div>
          </div>
        );

      case 'ASI / Feat':
        return (
          <div>
            <h3 style={sectionTitle}>Ability Score Improvement</h3>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <button
                onClick={() => setAsiMode('asi')}
                style={{
                  ...tabStyle,
                  borderBottomColor: asiMode === 'asi' ? GOLD : 'transparent',
                  color: asiMode === 'asi' ? GOLD : 'var(--color-text-muted)',
                }}
              >
                +2 Ability Scores
              </button>
              <button
                onClick={() => setAsiMode('feat')}
                style={{
                  ...tabStyle,
                  borderBottomColor: asiMode === 'feat' ? GOLD : 'transparent',
                  color: asiMode === 'feat' ? GOLD : 'var(--color-text-muted)',
                }}
              >
                Feat
              </button>
            </div>

            {asiMode === 'asi' ? (
              <div>
                <p style={descStyle}>Choose two ability scores to increase by +1 each (or the same one for +2). Max 20.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                  {['First +1', 'Second +1'].map((label, idx) => (
                    <div key={idx}>
                      <label style={labelStyle}>{label}</label>
                      <select
                        value={idx === 0 ? asi1 : asi2}
                        onChange={(e) => idx === 0 ? setAsi1(e.target.value) : setAsi2(e.target.value)}
                        style={selectStyle}
                      >
                        {ABILITIES.map((ab) => {
                          const score = character.abilityScores[ab];
                          const atCap = score >= 20;
                          return (
                            <option key={ab} value={ab} disabled={atCap}>
                              {ABILITY_SHORT[ab]} ({score}{atCap ? ' MAX' : ''})
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <p style={descStyle}>Enter the feat name. Apply mechanical effects manually.</p>
                <input
                  value={featName}
                  onChange={(e) => setFeatName(e.target.value)}
                  placeholder="e.g., Sharpshooter, War Caster, Lucky"
                  style={inputStyle}
                />
              </div>
            )}
          </div>
        );

      case 'Subclass':
        return (
          <div>
            <h3 style={sectionTitle}>Choose Subclass</h3>
            <p style={descStyle}>
              At {preview?.classBeingLeveled} level {preview?.newClassLevel}, you choose your specialization.
            </p>
            <input
              value={subclass}
              onChange={(e) => setSubclass(e.target.value)}
              placeholder={`e.g., ${preview?.classBeingLeveled === 'Fighter' ? 'Champion, Battle Master, Eldritch Knight' : 'Enter subclass name'}`}
              style={inputStyle}
            />
          </div>
        );

      case 'Features':
        return (
          <div>
            <h3 style={sectionTitle}>New Features</h3>
            {preview && preview.newFeatures.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {preview.newFeatures.map((f, i) => (
                  <div key={i} style={featureCardStyle}>
                    <div style={{ fontWeight: 600, color: GOLD, marginBottom: 4 }}>{f.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                      {f.description}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={descStyle}>No new features at this level.</p>
            )}
          </div>
        );

      case 'Spells':
        return (
          <div>
            <h3 style={sectionTitle}>Spellcasting</h3>
            {preview?.newSpellSlots && Object.keys(preview.newSpellSlots).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={labelStyle}>Spell Slots</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(preview.newSpellSlots).map(([level, count]) => (
                    <div key={level} style={slotBadgeStyle}>
                      Level {level}: {count} slot{count !== 1 ? 's' : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={labelStyle}>Learn New Spells</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                value={newSpellInput}
                onChange={(e) => setNewSpellInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSpellInput.trim()) {
                    setNewSpells([...newSpells, newSpellInput.trim()]);
                    setNewSpellInput('');
                  }
                }}
                placeholder="Type spell name and press Enter"
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
            {newSpells.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {newSpells.map((sp, i) => (
                  <span
                    key={i}
                    style={spellTagStyle}
                    onClick={() => setNewSpells(newSpells.filter((_, j) => j !== i))}
                    title="Click to remove"
                  >
                    {sp} ×
                  </span>
                ))}
              </div>
            )}
          </div>
        );

      case 'Review':
        return (
          <div>
            <h3 style={sectionTitle}>Level Up Summary</h3>
            <div style={reviewCardStyle}>
              <div style={reviewRow}>
                <span>Class</span>
                <span style={{ color: GOLD, fontWeight: 600 }}>
                  {preview?.classBeingLeveled} {(preview?.newClassLevel ?? 1) - 1} → {preview?.newClassLevel}
                </span>
              </div>
              <div style={reviewRow}>
                <span>Total Level</span>
                <span>{preview?.currentTotalLevel} → {preview?.newTotalLevel}</span>
              </div>
              <div style={reviewRow}>
                <span>HP</span>
                <span style={{ color: '#4ade80' }}>
                  +{hpGain} (new max: {character.maxHp + hpGain})
                </span>
              </div>
              {preview && preview.newProficiencyBonus !== character.proficiencyBonus && (
                <div style={reviewRow}>
                  <span>Proficiency Bonus</span>
                  <span style={{ color: GOLD }}>
                    +{character.proficiencyBonus} → +{preview.newProficiencyBonus}
                  </span>
                </div>
              )}
              {preview?.needsAsi && asiMode === 'asi' && (
                <div style={reviewRow}>
                  <span>ASI</span>
                  <span>{ABILITY_SHORT[asi1]} +1, {ABILITY_SHORT[asi2]} +1</span>
                </div>
              )}
              {preview?.needsAsi && asiMode === 'feat' && featName && (
                <div style={reviewRow}>
                  <span>Feat</span>
                  <span>{featName}</span>
                </div>
              )}
              {subclass && (
                <div style={reviewRow}>
                  <span>Subclass</span>
                  <span>{subclass}</span>
                </div>
              )}
              {preview && preview.newFeatures.length > 0 && (
                <div style={reviewRow}>
                  <span>New Features</span>
                  <span>{preview.newFeatures.map((f) => f.name).join(', ')}</span>
                </div>
              )}
              {newSpells.length > 0 && (
                <div style={reviewRow}>
                  <span>New Spells</span>
                  <span>{newSpells.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <Icon name="sparkle" size={18} color={GOLD} />
          <h2 style={{ margin: 0, fontSize: 16, fontFamily: 'Georgia, serif', color: GOLD }}>
            Level Up — {character.name}
          </h2>
          <button onClick={onClose} style={closeButtonStyle}>
            <Icon name="close" size={16} color="var(--color-text-muted)" />
          </button>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 4, padding: '0 24px 16px', justifyContent: 'center' }}>
          {steps.map((s, i) => (
            <div
              key={s}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: i <= step ? GOLD : GOLD_DIM,
                transition: 'background-color 0.2s',
              }}
              title={s}
            />
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '0 24px 24px', minHeight: 200 }}>
          {renderStep()}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button
            onClick={step === 0 ? onClose : prevStep}
            style={buttonStyle}
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {steps[step] === 'Review' ? (
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              style={confirmButtonStyle}
            >
              {isSubmitting ? 'Leveling Up...' : 'Confirm Level Up'}
            </button>
          ) : (
            <button onClick={nextStep} style={nextButtonStyle}>
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
};

const modalStyle: React.CSSProperties = {
  width: 520,
  maxHeight: '85vh',
  overflowY: 'auto',
  backgroundColor: 'var(--color-surface, #1a1520)',
  borderRadius: 12,
  border: `1px solid ${GOLD_DIM}`,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '20px 24px 12px',
};

const closeButtonStyle: React.CSSProperties = {
  marginLeft: 'auto',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px 24px',
  borderTop: `1px solid ${GOLD_DIM}`,
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 20px',
  fontSize: 13,
  fontFamily: 'Georgia, serif',
  backgroundColor: 'transparent',
  border: `1px solid var(--color-border, #333)`,
  borderRadius: 6,
  color: 'var(--color-text-secondary, #999)',
  cursor: 'pointer',
};

const nextButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  borderColor: GOLD,
  color: GOLD,
};

const confirmButtonStyle: React.CSSProperties = {
  padding: '8px 24px',
  fontSize: 13,
  fontFamily: 'Georgia, serif',
  fontWeight: 600,
  background: `linear-gradient(135deg, ${GOLD}, #b8962e)`,
  border: 'none',
  borderRadius: 6,
  color: '#1a1520',
  cursor: 'pointer',
};

const sectionTitle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 14,
  fontFamily: 'Georgia, serif',
  color: GOLD,
};

const descStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--color-text-secondary, #aaa)',
  lineHeight: 1.5,
  margin: 0,
};

const classCardStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '10px 14px',
  marginBottom: 8,
  border: '1px solid var(--color-border, #333)',
  borderRadius: 6,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
};

const optionButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '16px',
  border: '1px solid var(--color-border, #333)',
  borderRadius: 8,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  textAlign: 'center',
};

const tabStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  padding: '8px 16px',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'Georgia, serif',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--color-text-muted, #888)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 14,
  fontFamily: 'Georgia, serif',
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: `1px solid var(--color-border, #333)`,
  borderRadius: 6,
  color: 'var(--color-text, #eee)',
  outline: 'none',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const featureCardStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 6,
  border: `1px solid ${GOLD_DIM}`,
  backgroundColor: 'rgba(212, 175, 55, 0.05)',
};

const slotBadgeStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 4,
  backgroundColor: 'rgba(147, 51, 234, 0.15)',
  color: '#c084fc',
  fontSize: 12,
};

const spellTagStyle: React.CSSProperties = {
  padding: '3px 8px',
  borderRadius: 4,
  backgroundColor: 'rgba(147, 51, 234, 0.2)',
  color: '#c084fc',
  fontSize: 12,
  cursor: 'pointer',
};

const reviewCardStyle: React.CSSProperties = {
  borderRadius: 8,
  border: `1px solid ${GOLD_DIM}`,
  overflow: 'hidden',
};

const reviewRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: `1px solid rgba(255,255,255,0.05)`,
  fontSize: 13,
  color: 'var(--color-text, #eee)',
};
